import { useEffect, useState } from "react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { LOGO_URL, COMPANY } from "@/lib/siteData";
import { useTheme } from "@/lib/theme";
import LeadDialog from "@/components/site/LeadDialog";

const NAV = [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Portfolio", href: "#portfolio" },
    { label: "Process", href: "#process" },
    { label: "Contact", href: "#contact" },
];

export default function Header() {
    const { theme, toggle } = useTheme();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
                scrolled ? "glass py-2" : "py-4 bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
                <a href="#top" data-testid="brand-link" className="flex items-center gap-3">
                    <img
                        src={LOGO_URL}
                        alt="CHANDAK BuildVision"
                        className="h-10 w-10 md:h-11 md:w-11 object-contain"
                    />
                    <div className="leading-tight">
                        <div className="font-display text-base md:text-lg tracking-tight">
                            CHANDAK <span className="text-gold">BuildVision</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                            {COMPANY.tagline}
                        </div>
                    </div>
                </a>

                <nav className="hidden lg:flex items-center gap-9">
                    {NAV.map((n) => (
                        <a
                            key={n.href}
                            href={n.href}
                            data-testid={`nav-${n.label.toLowerCase()}`}
                            className="link-hover text-sm font-medium text-foreground/80 hover:text-foreground"
                        >
                            {n.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2 md:gap-3">
                    <button
                        onClick={toggle}
                        data-testid="theme-toggle"
                        aria-label="Toggle theme"
                        className="h-10 w-10 rounded-full grid place-items-center border hairline hover:border-gold transition-colors"
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>

                    <LeadDialog
                        trigger={
                            <button
                                data-testid="header-cta"
                                className="hidden md:inline-flex items-center gap-2 h-10 px-5 rounded-full bg-gold text-white text-sm font-medium hover:brightness-110 transition"
                            >
                                Book Consultation
                            </button>
                        }
                    />

                    <button
                        className="lg:hidden h-10 w-10 grid place-items-center border hairline rounded-full"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden glass mx-4 mt-2 rounded-2xl p-6" data-testid="mobile-menu">
                    <div className="flex flex-col gap-5">
                        {NAV.map((n) => (
                            <a
                                key={n.href}
                                href={n.href}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                className="text-lg font-display"
                            >
                                {n.label}
                            </a>
                        ))}
                        <LeadDialog
                            trigger={
                                <button
                                    data-testid="mobile-cta"
                                    className="mt-2 h-11 rounded-full bg-gold text-white text-sm font-medium"
                                >
                                    Book Consultation
                                </button>
                            }
                        />
                    </div>
                </div>
            )}
        </header>
    );
}
